import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth, formatApiError } from "@/lib/auth";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(""); // Bug #9 — message d'erreur inline persistant
  const [emailTaken, setEmailTaken] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setEmailTaken(false);
    try {
      await register(form);
      toast.success("Compte créé");
      nav("/espace", { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const msg = formatApiError(err);
      const isDup = status === 409 || /existe|déjà|utilis/i.test(msg || "");
      if (isDup) {
        setEmailTaken(true);
        setError("Cet email est déjà utilisé. Connectez-vous ou utilisez une autre adresse.");
      } else {
        setError(msg);
      }
      // Bug #9 — on garde les champs remplis (sauf le mot de passe) pour éviter la double saisie
      setForm((f) => ({ ...f, password: "" }));
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const set = (k) => (e) => {
    setError("");
    setEmailTaken(false);
    setForm({ ...form, [k]: e.target.value });
  };

  return (
    <div className="cs-cream min-h-[80vh] py-16" data-testid="page-register">
      <div className="max-w-md mx-auto px-6">
        <div className="cs-eyebrow text-neutral-500">Espace client</div>
        <h1 className="cs-h1 mt-3 text-5xl">Créer un compte.</h1>
        <div className="mt-6 border-l-2 border-[#0E7C90] pl-4 text-[13px] text-neutral-700 leading-relaxed" data-testid="register-intermediary-info">
          <strong>Agence ou intermédiaire ?</strong> Un seul compte suffit pour commander pour vos propres projets et pour ceux
          de vos clients. Vous enregistrez vos clients finaux sans leur créer de compte, et vous choisissez sur chaque commande
          qui reçoit la facture. Vous gardez le suivi complet du projet.
        </div>
        <form className="mt-10 space-y-5" onSubmit={submit} noValidate>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="register-first" className="cs-eyebrow text-neutral-500">Prénom</label>
              <input id="register-first" name="first_name" required value={form.first_name} onChange={set("first_name")} className="cs-input mt-2" data-testid="register-first" autoComplete="given-name" />
            </div>
            <div>
              <label htmlFor="register-last" className="cs-eyebrow text-neutral-500">Nom</label>
              <input id="register-last" name="last_name" required value={form.last_name} onChange={set("last_name")} className="cs-input mt-2" data-testid="register-last" autoComplete="family-name" />
            </div>
          </div>
          <div>
            <label htmlFor="register-email" className="cs-eyebrow text-neutral-500">Email</label>
            <input
              id="register-email"
              name="email"
              type="email"
              required
              value={form.email}
              onChange={set("email")}
              aria-invalid={emailTaken || undefined}
              aria-describedby={error ? "register-error" : undefined}
              className={`cs-input mt-2 ${emailTaken ? "!border-red-500" : ""}`}
              data-testid="register-email"
              autoComplete="email"
            />
          </div>
          <div>
            <label htmlFor="register-password" className="cs-eyebrow text-neutral-500">Mot de passe (8+ caractères)</label>
            <input id="register-password" name="password" type="password" required minLength={8} value={form.password} onChange={set("password")} className="cs-input mt-2" data-testid="register-password" autoComplete="new-password" />
          </div>
          <p className="text-[12px] font-mono text-neutral-500 leading-relaxed" data-testid="register-rgpd">
            En créant un compte, vos données (nom, prénom, email) sont utilisées uniquement pour le suivi de vos commandes
            et notre correspondance. Consultez notre <Link to="/confidentialite" className="cs-link-underline">politique de confidentialité</Link>.
          </p>

          {error && (
            <div
              id="register-error"
              role="alert"
              aria-live="polite"
              className="text-[13px] text-red-700 border border-red-400 bg-red-50 px-3 py-2"
              data-testid="register-error"
            >
              {error}
              {emailTaken && (
                <div className="mt-1">
                  <Link to={`/connexion?email=${encodeURIComponent(form.email)}`} className="cs-link-underline text-[#0E7C90]" data-testid="register-goto-login">
                    → Aller à la page de connexion
                  </Link>
                </div>
              )}
            </div>
          )}

          <button type="submit" disabled={loading} className="cs-btn-primary w-full justify-center" data-testid="register-submit">
            {loading ? "Création…" : "Créer mon compte"}
          </button>
          <p className="text-sm text-neutral-600 pt-3">
            Déjà un compte ? <Link to="/connexion" className="cs-link-underline text-[#0E7C90]">Se connecter</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
